import { router, useForm } from "@inertiajs/react"
import { Check, Copy, Edit, ExternalLink, Mail, Plus, Trash2, X } from "lucide-react"
import { type ReactNode, useState } from "react"

import { AdminLayout } from "@/layouts/AdminLayout"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type {
  GuestProfileDetail,
  GuideSummaryRow,
  PropertySummary,
  StayRow,
} from "@/lib/types"

interface Props {
  property: PropertySummary
  profile: GuestProfileDetail
  stays: StayRow[]
  guides: GuideSummaryRow[]
  has_default_guide: boolean
  new_share_url: string | null
  urls: {
    index: string
    edit: string
    delete: string
    stays_create: string
    personalize: string
  }
}

function StayStatusBadge({ status }: { status: StayRow["status"] }) {
  const variants: Record<StayRow["status"], "default" | "secondary" | "outline"> = {
    upcoming:  "secondary",
    active:    "default",
    completed: "outline",
  }
  return <Badge variant={variants[status]}>{status}</Badge>
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Button variant="outline" size="sm" onClick={handleCopy} className="gap-1.5">
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy link"}
    </Button>
  )
}

function SendButton({
  guide,
  hasEmail,
}: {
  guide: GuideSummaryRow
  hasEmail: boolean
}) {
  const [sending, setSending] = useState(false)

  function handleSend() {
    if (!guide.send_url) return
    setSending(true)
    router.post(guide.send_url, {}, {
      preserveScroll: true,
      onFinish: () => setSending(false),
    })
  }

  const label = guide.sent_at ? "Resend" : "Send to guest"

  if (!hasEmail) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0}>
              <Button variant="outline" size="sm" className="gap-1.5" disabled>
                <Mail className="h-3.5 w-3.5" />
                {label}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>No email on file</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={handleSend}
        disabled={sending}
      >
        <Mail className="h-3.5 w-3.5" />
        {sending ? "Sending…" : label}
      </Button>
      {guide.sent_at && (
        <span className="text-xs text-muted-foreground">
          Sent {new Date(guide.sent_at).toLocaleDateString()}
        </span>
      )}
    </div>
  )
}

function NewShareBanner({
  url,
  onDismiss,
}: {
  url: string
  onDismiss: () => void
}) {
  const [copied, setCopied] = useState(false)

  function handleCopy() {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 dark:border-green-900 dark:bg-green-950/30">
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-green-800 dark:text-green-200">
          Guide created — share link ready
        </p>
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-green-700 dark:text-green-300 truncate max-w-xs">
            {url}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-6 gap-1 border-green-300 text-green-700 hover:bg-green-100 dark:border-green-700 dark:text-green-300"
            onClick={handleCopy}
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 text-green-600 hover:text-green-800 dark:text-green-400"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

export default function GuestProfileShow({
  property,
  profile,
  stays,
  guides,
  has_default_guide,
  new_share_url,
  urls,
}: Props) {
  const [stayOpen, setStayOpen] = useState(false)
  const [editingStay, setEditingStay] = useState<StayRow | null>(null)
  const [personalizeOpen, setPersonalizeOpen] = useState(false)
  const [personalizeProcessing, setPersonalizeProcessing] = useState(false)
  const [bannerUrl, setBannerUrl] = useState<string | null>(new_share_url)
  const [selectedStayId, setSelectedStayId] = useState<string>(
    stays.length === 1 ? String(stays[0].id) : ""
  )
  const { data, setData, post, patch, processing, errors, reset } = useForm({
    check_in_on: "",
    check_out_on: "",
    room_number: "",
    status: "upcoming" as "upcoming" | "active" | "completed",
    mood_context: "",
  })

  function openCreateStay() {
    reset()
    setEditingStay(null)
    setStayOpen(true)
  }

  function openEditStay(stay: StayRow) {
    setData({
      check_in_on: stay.check_in_on,
      check_out_on: stay.check_out_on,
      room_number: stay.room_number ?? "",
      status: stay.status,
      mood_context: stay.mood_context ?? "",
    })
    setEditingStay(stay)
    setStayOpen(true)
  }

  function closeStaySheet() {
    setStayOpen(false)
    setEditingStay(null)
  }

  function handleStaySubmit(e: React.FormEvent) {
    e.preventDefault()
    if (editingStay) {
      patch(editingStay.update_url, {
        onSuccess: () => { setStayOpen(false); setEditingStay(null); reset() },
      })
    } else {
      post(urls.stays_create, {
        onSuccess: () => { setStayOpen(false); reset() },
      })
    }
  }

  function handleDeleteStay(stay: StayRow) {
    router.delete(stay.delete_url)
  }

  function handleDelete() {
    router.delete(urls.delete, {
      onSuccess: () => router.visit(urls.index),
    })
  }

  const canPersonalize = has_default_guide && stays.length > 0

  const personalizeTooltip = !has_default_guide
    ? "Create a property default guide first"
    : stays.length === 0
    ? "Add a stay first"
    : undefined

  function handlePersonalize() {
    setPersonalizeProcessing(true)
    router.post(
      urls.personalize,
      { stay_id: selectedStayId || null, guest_profile_id: profile.id },
      {
        onError: () => setPersonalizeProcessing(false),
        onFinish: () => setPersonalizeProcessing(false),
      }
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{profile.name}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">{property.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => router.visit(urls.edit)}>
            <Edit className="h-4 w-4 mr-1.5" />
            Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete guest profile?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will remove all personal information for {profile.name}. Stay and guide
                  history will be retained anonymously. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Yes, delete guest
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Profile card */}
      <div className="rounded-lg border p-5 space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground">Profile details</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Email</p>
            <p className="font-medium">{profile.email ?? "—"}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Phone</p>
            <p className="font-medium">{profile.phone ?? "—"}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Language</p>
            <p className="font-medium">{profile.language ?? "—"}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Last updated</p>
            <p className="font-medium">
              {new Date(profile.updated_at).toLocaleDateString("en-GB")}
            </p>
          </div>
          {profile.notes && (
            <div className="col-span-2">
              <p className="text-muted-foreground">Notes</p>
              <p className="font-medium">{profile.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* New share URL banner */}
      {bannerUrl && (
        <NewShareBanner url={bannerUrl} onDismiss={() => setBannerUrl(null)} />
      )}

      {/* Stays section */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">Stays ({stays.length})</h2>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={openCreateStay}
          >
            <Plus className="h-4 w-4" />
            Add Stay
          </Button>
        </div>

        {stays.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center">
            <p className="text-sm text-muted-foreground">No stays recorded yet.</p>
          </div>
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Check-in</TableHead>
                  <TableHead>Check-out</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Room</TableHead>
                  <TableHead>Season</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {stays.map((stay) => (
                  <TableRow key={stay.id}>
                    <TableCell>{new Date(stay.check_in_on).toLocaleDateString("en-GB")}</TableCell>
                    <TableCell>{new Date(stay.check_out_on).toLocaleDateString("en-GB")}</TableCell>
                    <TableCell><StayStatusBadge status={stay.status} /></TableCell>
                    <TableCell className="text-muted-foreground">{stay.room_number ?? "—"}</TableCell>
                    <TableCell className="text-muted-foreground capitalize">{stay.season ?? "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditStay(stay)}
                          aria-label="Edit stay"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              aria-label="Delete stay"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete stay?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently remove the stay record for{" "}
                                {new Date(stay.check_in_on).toLocaleDateString("en-GB")}
                                {" "}–{" "}
                                {new Date(stay.check_out_on).toLocaleDateString("en-GB")}.
                                This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteStay(stay)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Yes, delete stay
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Stay Sheet */}
      <Sheet open={stayOpen} onOpenChange={(open) => !open && closeStaySheet()}>
        <SheetContent side="right" className="w-[420px] overflow-y-auto sm:max-w-[420px]">
          <SheetHeader>
            <SheetTitle>{editingStay ? "Edit Stay" : "Add Stay"}</SheetTitle>
          </SheetHeader>
          <form onSubmit={handleStaySubmit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="stay_check_in_on">Check-in date</Label>
              <Input
                id="stay_check_in_on"
                type="date"
                value={data.check_in_on}
                onChange={(e) => setData("check_in_on", e.target.value)}
                required
              />
              {errors.check_in_on && <p className="text-xs text-destructive">{errors.check_in_on}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="stay_check_out_on">Check-out date</Label>
              <Input
                id="stay_check_out_on"
                type="date"
                value={data.check_out_on}
                onChange={(e) => setData("check_out_on", e.target.value)}
                required
              />
              {errors.check_out_on && <p className="text-xs text-destructive">{errors.check_out_on}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="stay_room_number">Room number</Label>
              <Input
                id="stay_room_number"
                value={data.room_number}
                onChange={(e) => setData("room_number", e.target.value)}
                placeholder="e.g. 204"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="stay_status">Status</Label>
              <Select
                value={data.status}
                onValueChange={(v) =>
                  setData("status", v as typeof data.status)
                }
              >
                <SelectTrigger id="stay_status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="stay_mood_context">Guest mood / preferences</Label>
              <Input
                id="stay_mood_context"
                value={data.mood_context}
                onChange={(e) => setData("mood_context", e.target.value)}
                placeholder="Optional notes"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={closeStaySheet}>
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? "Saving…" : editingStay ? "Save changes" : "Add stay"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Guides section */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">Personalized guides ({guides.length})</h2>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={!canPersonalize ? 0 : undefined}>
                  <Dialog open={personalizeOpen} onOpenChange={setPersonalizeOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        disabled={!canPersonalize}
                      >
                        <ExternalLink className="h-4 w-4" />
                        Generate personalized guide
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Generate Personalized Guide</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-2">
                        <p className="text-sm text-muted-foreground">
                          A personalized copy of the property default guide will be created for{" "}
                          <span className="font-medium text-foreground">{profile.name}</span>.
                        </p>
                        {stays.length > 1 && (
                          <div className="space-y-1.5">
                            <Label htmlFor="personalize_stay">Link to stay</Label>
                            <Select
                              value={selectedStayId}
                              onValueChange={setSelectedStayId}
                            >
                              <SelectTrigger id="personalize_stay">
                                <SelectValue placeholder="Select a stay (optional)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="">No stay selected</SelectItem>
                                {stays.map((stay) => (
                                  <SelectItem key={stay.id} value={String(stay.id)}>
                                    {new Date(stay.check_in_on).toLocaleDateString("en-GB")}
                                    {" "}–{" "}
                                    {new Date(stay.check_out_on).toLocaleDateString("en-GB")}
                                    {stay.room_number ? ` · Room ${stay.room_number}` : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() => setPersonalizeOpen(false)}
                          disabled={personalizeProcessing}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handlePersonalize}
                          disabled={personalizeProcessing}
                        >
                          {personalizeProcessing ? "Generating…" : "Generate guide"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </span>
              </TooltipTrigger>
              {personalizeTooltip && (
                <TooltipContent>
                  <p>{personalizeTooltip}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>

        {guides.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center">
            <p className="text-sm text-muted-foreground">No personalized guides yet.</p>
            {!has_default_guide && (
              <p className="mt-1 text-xs text-muted-foreground">
                Set up a default guide for this property first.
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {guides.map((guide) => (
              <div key={guide.id} className="rounded-lg border p-4 flex flex-col gap-3">
                {/* Guide title + status */}
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium">{guide.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {guide.items_count} {guide.items_count === 1 ? "item" : "items"} ·{" "}
                      Created {new Date(guide.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant={guide.status === "active" ? "default" : "secondary"} className="capitalize shrink-0">
                    {guide.status}
                  </Badge>
                </div>

                {/* Share URL row */}
                {guide.share_url && (
                  <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2">
                    <span
                      className="flex-1 truncate font-mono text-xs text-muted-foreground"
                      title={guide.share_url}
                    >
                      {guide.share_url}
                    </span>
                    <CopyButton text={guide.share_url} />
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 flex-wrap">
                  <SendButton guide={guide} hasEmail={!!profile.email} />
                  {guide.edit_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => router.visit(guide.edit_url!)}
                      aria-label={`Edit ${guide.title}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

GuestProfileShow.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
